import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, Truck } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function MaterialMovementsPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [locations, setLocations] = useState([]);
  const [report, setReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ material_id: "", from_location_id: "", to_location_id: "", qty_type: "", quantity: "", loads: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [entries, lookups, r] = await Promise.all([
        api.get(`/reports/${reportId}/material-movements`),
        api.get("/lookups"),
        api.get(`/reports/${reportId}`),
      ]);
      setItems(entries || []);
      setMaterials(lookups.materials || []);
      setLocations(lookups.locations || []);
      setReport(r);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [reportId]);

  const isReadOnly = report?.status === "Submitted";
  const getName = (list, id) => list.find(i => i.id === id)?.name || "—";

  const openAdd = () => {
    setEditItem(null);
    setForm({ material_id: "", from_location_id: "", to_location_id: "", qty_type: "", quantity: "", loads: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      material_id: item.material_id || "",
      from_location_id: item.from_location_id || "",
      to_location_id: item.to_location_id || "",
      qty_type: item.qty_type || "",
      quantity: String(item.quantity || ""),
      loads: String(item.loads || ""),
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.material_id || !form.from_location_id || !form.to_location_id || !form.qty_type || !form.quantity) {
      toast.error("Material, locations, unit type, and quantity are required");
      return;
    }
    setSaving(true);
    const payload = { ...form, quantity: parseFloat(form.quantity) || 0, loads: parseInt(form.loads) || 0 };
    try {
      if (editItem) {
        await api.put(`/reports/${reportId}/material-movements/${editItem.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post(`/reports/${reportId}/material-movements`, payload);
        toast.success("Entry added");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reports/${reportId}/material-movements/${id}`);
      toast.success("Entry deleted");
      await loadData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen" data-testid="material-movements-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate(`/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Dashboard
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
              <Truck className="h-5 w-5 text-amber-400" strokeWidth={1.5} /> Material Movements
            </h1>
            <p className="text-muted-foreground text-sm mt-1"><span className="kpi-value">{items.length}</span> entries</p>
          </div>
          {!isReadOnly && (
            <Button onClick={openAdd} data-testid="add-material-button" className="font-headline font-semibold">
              <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground text-sm">No material movements logged yet</CardContent></Card>
        ) : (
          <Card className="border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs uppercase">Material</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">From</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">To</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Qty</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Unit</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Loads</TableHead>
                  {!isReadOnly && <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} className="border-border" data-testid={`material-row-${item.id}`}>
                    <TableCell className="font-medium text-sm">{getName(materials, item.material_id)}</TableCell>
                    <TableCell className="text-sm">{getName(locations, item.from_location_id)}</TableCell>
                    <TableCell className="text-sm">{getName(locations, item.to_location_id)}</TableCell>
                    <TableCell className="kpi-value text-sm">{item.quantity}</TableCell>
                    <TableCell className="text-sm">{item.qty_type}</TableCell>
                    <TableCell className="font-mono text-sm">{item.loads || "—"}</TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md" data-testid="material-dialog">
            <DialogHeader><DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} Material Movement</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Material *</Label>
                <Select value={form.material_id} onValueChange={(v) => setForm({ ...form, material_id: v })}>
                  <SelectTrigger data-testid="mat-material-select" className="bg-background border-border"><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.category})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">From *</Label>
                  <Select value={form.from_location_id} onValueChange={(v) => setForm({ ...form, from_location_id: v })}>
                    <SelectTrigger data-testid="mat-from-select" className="bg-background border-border"><SelectValue placeholder="From" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">To *</Label>
                  <Select value={form.to_location_id} onValueChange={(v) => setForm({ ...form, to_location_id: v })}>
                    <SelectTrigger data-testid="mat-to-select" className="bg-background border-border"><SelectValue placeholder="To" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Unit Type *</Label>
                <Select value={form.qty_type} onValueChange={(v) => setForm({ ...form, qty_type: v })}>
                  <SelectTrigger data-testid="mat-unit-select" className="bg-background border-border"><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="Tons">Tons</SelectItem>
                    <SelectItem value="CYD">CYD (Cubic Yards)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Quantity *</Label>
                  <Input data-testid="mat-quantity" type="number" step="0.01" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-background border-border font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Loads</Label>
                  <Input data-testid="mat-loads" type="number" min="0" value={form.loads} onChange={(e) => setForm({ ...form, loads: e.target.value })} className="bg-background border-border font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Notes</Label>
                <Input data-testid="mat-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className="bg-background border-border" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-material-button" className="font-headline font-semibold">{saving ? "Saving..." : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
